import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from 'services/context';

import Container from 'components/Container';
import Question from 'pages/Quiz/Question';
import Button from 'components/Button';
import Modal from 'components/Modal';
import AnswerFeedback from './AnswerFeedback';

import { ROUTES } from 'constants/index.js';
import { COURSES_ACTIONS } from 'services/reducers/actionTypes';
import { useTranslation } from 'services/hooks';

import './style.css';

function Quiz() {
	const { Api, dispatchCourses, courses } = useAppContext();
	const { chapterId, recordId } = useParams();
	const navigate = useNavigate();
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'quiz';

	const [test, setTest] = useState(undefined);
	const [userAnswers, setUserAnswers] = useState({});
	const [paramChapterId, setParamChapterId] = useState(undefined);
	const [feedback, setFeedback] = useState(undefined);
	const [passedTest, setPassedTest] = useState(false);

	const validateQuiz = (testToValidate, answersToValidate) => {
		const validatedTest = { ...testToValidate };
		let passed = true;
		let firstIncorrectNumber;

		const rightAnswers = testToValidate?.courseTestQuestions?.map(({ courseTestQuestionAnswers }) =>
			courseTestQuestionAnswers.find(answer => answer.isCorrectAnswer)
		);

		if (!rightAnswers) return;

		//check for user answers
		validatedTest.courseTestQuestions = validatedTest.courseTestQuestions.map(question => {
			const currentRightAnswer = rightAnswers.find(answer => answer.questionId === question.questionId);
			let isValid = answersToValidate[currentRightAnswer.questionId] === currentRightAnswer.answerId;
			if (isValid) question.answerStatus = 'correct';
			else {
				question.answerStatus = 'incorrect';
				firstIncorrectNumber = firstIncorrectNumber || question.questionId;
				passed = false;
			}

			return question;
		});

		return { validatedTest, passed, firstIncorrectNumber };
	};

	useEffect(() => {
		try {
			if (paramChapterId === chapterId) return;
			setPassedTest(false);
			setParamChapterId(chapterId);
			(async () => {
				const courseTest = await Api.GetTestForCourseChapter(courses?.currentCourse?.courseId, chapterId);
				if (!courseTest) return;

				if (courseTest.isFinal) {
					Api.UpdateTestStatus(recordId);
				}

				const prevAnswers = await Api.GetStudentAnswers(courseTest.testId, recordId);
				if (!prevAnswers) {
					setTest(courseTest);
					return;
				}

				//update user answers cache
				const newAnswers = prevAnswers.reduce((prevAns, curr) => {
					prevAns[curr.questionId] = curr.answerId;
					return prevAns;
				}, {});

				const { validatedTest, passed } = validateQuiz(courseTest, newAnswers);

				setPassedTest(passed);

				//check user previous answers - and update view
				validatedTest.courseTestQuestions.forEach(question => {
					const userOldAnswer = prevAnswers.find(a => a.questionId === question.questionId);
					question.courseTestQuestionAnswers = question.courseTestQuestionAnswers.map(answer => {
						answer.checked = userOldAnswer.answerId === answer.answerId;
						return answer;
					});
				});

				setUserAnswers(newAnswers);
				setTest(validatedTest);
			})();
		} catch (error) {
			console.warn(error);
		}
	}, [chapterId, recordId]);

	const _handelQuizSubmit = async e => {
		e?.preventDefault();
		setPassedTest(false);
		if (Object.keys(userAnswers).length !== test.courseTestQuestions.length) {
			setFeedback('missing');
			window.scrollTo({ top: 0, behavior: 'smooth' });
			return;
		}

		const { validatedTest, passed, firstIncorrectNumber } = validateQuiz(test, userAnswers);

		setPassedTest(passed);

		setTest(validatedTest);

		const updateTestReqBody = {
			recordId: parseInt(recordId),
			chapterId: parseInt(chapterId),
			isFinal: test.isFinal,
			userAnswers: Object.keys(userAnswers).map(key => ({ questionId: key, answerId: userAnswers[key] })),
		};

		const serverPassed = await Api.UpdateTestStudent(updateTestReqBody);

		if (passed && serverPassed) {
			setFeedback('correct');
			const userCourses = await Api.GetUserCourses();
			if (userCourses?.length > 0) dispatchCourses({ type: COURSES_ACTIONS.SET_COURSES, payload: userCourses });
			return;
		}

		//scroll to first wrong answer
		const firstWrongQuestion = e.target.querySelector(`[question-number="${firstIncorrectNumber}"]`);
		if (firstWrongQuestion) firstWrongQuestion.scrollIntoView({ behavior: 'smooth', block: 'center' });
		setFeedback('incorrect');
	};

	const _handleUserAnswer = ({ answerId, questionId }) => {
		setPassedTest(false);
		const tempTest = { ...test };
		const question = tempTest.courseTestQuestions.find(ques => ques.questionId === questionId);
		delete question.answerStatus;
		question.courseTestQuestionAnswers = question.courseTestQuestionAnswers.map(answer => {
			if (answer.answerId === answerId) answer.checked = true;
			else answer.checked = false;
			return answer;
		});

		const newAnswers = { ...userAnswers };
		newAnswers[questionId] = answerId;

		setTest(tempTest);

		setUserAnswers(newAnswers);
	};

	const _handleFeedbackButtonClick = userFeedback => {
		setFeedback(undefined); // userFeedback "correct" | "incorrect" string
		if (userFeedback !== 'correct') return;
		const currentChapter = courses?.currentCourse?.courseChapters.find(chapter => chapter.courseChapterId === parseInt(chapterId));
		//navigate to course page when completed or
		if (test.isFinal || !currentChapter?.nextChapterId) {
			navigate(`/${ROUTES.COURSE}/${recordId}`);
			return;
		}

		// navigate to relevante path
		const path = generatePath(currentChapter.chapterURL, {
			recordId,
			chapterId: currentChapter.nextChapterId,
		});
		navigate(path);
	};

	const _renderQuestion = ({
		questionsText,
		questionId,
		courseTestQuestionAnswers,
		answerStatus,
		questionNumberInTest,
		questionAudioLink,
		questionImageLink,
		questionInstruction,
		questionParagraph,
	}) => (
		<Question
			questionNumberInTest={questionNumberInTest}
			key={questionsText}
			id={questionId}
			title={questionsText}
			answers={courseTestQuestionAnswers}
			answerStatus={answerStatus}
			onAnswer={_handleUserAnswer}
			disabled={answerStatus === 'correct'}
			questionAudioLink={questionAudioLink}
			questionImageLink={questionImageLink}
			questionInstruction={questionInstruction}
			questionParagraph={questionParagraph}
		/>
	);

	const _handlePressbackButton = () => {
		try {
			const currentChapter = courses?.currentCourse?.courseChapters.find(chapter => chapter.courseChapterId === parseInt(chapterId));
			const path = generatePath(currentChapter.chapterURL, {
				chapterId,
				recordId,
			});
			navigate(path);
		} catch (err) {}
	};
	return (
		<Container className="Quiz">
			{test && (
				<>
					<div className="Quiz__title">{test?.testDescriptionText || ''}</div>

					<Button
						small
						title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_chapter_button`)}
						startIcon="clipboardIcon"
						onClick={_handlePressbackButton}
						extraClassName="transparent"
					/>
					<form onSubmit={_handelQuizSubmit}>
						{test && test?.courseTestQuestions?.map(_renderQuestion)}
						<Button
							title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_results_button`)}
							endIcon="rightArrow"
							disabled={passedTest}
						/>
					</form>
					<Modal isVisible={feedback} setVisibility={() => setFeedback(undefined)}>
						<AnswerFeedback feedback={feedback} onClick={_handleFeedbackButtonClick} isFinal={test?.isFinal || false} />
					</Modal>
				</>
			)}
		</Container>
	);
}

export default Quiz;
